import React, { useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import { saveAs } from 'file-saver';
import Papa from 'papaparse';
import JSZip from 'jszip';
import { secondsToTimeText } from '../../../../../platform/ux/Advertiser/utils/secondsToTimeText';

const filterByDateRange = (item, dateRange) => {
  if (!dateRange) return true;
  return (
    moment(item.date, 'YYYY-MM-DD').isSameOrAfter(dateRange[0]) &&
    moment(item.date, 'YYYY-MM-DD').isSameOrBefore(dateRange[1])
  );
};

const SessionsController = ({
  sessions,
  sessionsList,
  loading,
  error,
  sessionsError,
  sessionsLoading,
  getAllSessions,
  allSessionsData,
  allSessionsLoading,
  allSessionsError,
  fetchSessionDetails,
  expandedRowsData,
  setExpandedRowsData,
  sessionDetailsLoading,
  currentDateRange,
  setCurrentDateRange,
  campaignOrder,
  setExportButtonLoading,
  setExportButtonDisable,
  setExportButtonMenuItems,
  buildSessionsWhereClause,
  match,
  history,
  location,
  ...props
}) => {
  const [summaryData, setSummaryData] = useState({
    sessions: sessions?.sessions || 0,
    averageSessionDuration: sessions?.avgSessionDuration || 0,
    pagesPerSession: sessions?.pagesPerSession || 0,
  });
  const [isExportRequested, setIsExportRequested] = useState(false);

  const changeDatePicker = (dates) => {
    if (setCurrentDateRange) {
      setCurrentDateRange(dates);
    }

    // Reset to page 1 when date filter changes
    const { id } = match.params;
    const searchParams = new URLSearchParams(location.search);
    history.push({
      pathname: `/campaigns/details/${id}/1`,
      search: searchParams.toString(),
    });
  };

  // CSV export menu items
  useEffect(() => {
    if (!setExportButtonMenuItems || !setExportButtonDisable) return;

    if (campaignOrder) {
      setExportButtonDisable(false);
      setExportButtonMenuItems([
        {
          key: 'sessionsByDate',
          text: 'Sessions - By Date',
          tooltip: 'Export sessions data grouped by date.',
          onClick: () => {
            setExportButtonLoading(true);
            try {
              const zip = new JSZip();
              const folder = zip.folder(
                `${campaignOrder.name.replace(/[\W_]/g, '_')}-Sessions_By_Date`
              );

              const dataToExport = (sessions?.byDate || []).filter((item) =>
                filterByDateRange(item, currentDateRange)
              );

              if (dataToExport.length === 0) {
                setExportButtonLoading(false);
                return;
              }

              const exportData = dataToExport.map((item) => ({
                Date: item.date,
                Sessions: item.sessions || 0,
                Impressions: item.impressions || 0,
                'Duration (seconds)': item.totalSessionDuration || 0,
                'Avg Duration':
                  item.totalSessionDuration && item.sessions
                    ? secondsToTimeText(Math.round(item.totalSessionDuration / item.sessions))
                    : '0s',
                'Pages Per Session': item.pagesPerSession ? item.pagesPerSession.toFixed(2) : '0.00',
              }));

              folder.file(
                `${campaignOrder.name.replace(/[\W_]/g, '_')}-Sessions_By_Date.csv`,
                Papa.unparse(exportData)
              );

              zip.generateAsync({ type: 'blob' }).then(
                (blob) => {
                  saveAs(blob, `${campaignOrder.name.replace(/[\W_]/g, '_')}-Sessions_By_Date`);
                  setExportButtonLoading(false);
                },
                (error) => {
                  console.error('Export error:', error);
                  setExportButtonLoading(false);
                }
              );
            } catch (error) {
              console.error('CSV export error:', error);
              setExportButtonLoading(false);
            }
          },
        },
        {
          key: 'sessionsDeviceBreakdown',
          text: 'Sessions - Device Breakdown',
          tooltip: 'Export sessions data broken down by device type over time.',
          onClick: () => {
            setExportButtonLoading(true);
            try {
              const zip = new JSZip();
              const folder = zip.folder(
                `${campaignOrder.name.replace(/[\W_]/g, '_')}-Sessions_Device_Breakdown`
              );

              const dataToExport = (sessions?.byDateBreakdown || []).filter((item) =>
                filterByDateRange(item, currentDateRange)
              );

              if (dataToExport.length === 0) {
                setExportButtonLoading(false);
                return;
              }

              const exportData = dataToExport.map((item) => ({
                Date: item.date,
                Desktop: item.desktop || 0,
                Mobile: item.mobile || 0,
                Tablet: item.tablet || 0,
                'Total Sessions':
                  (item.desktop || 0) +
                  (item.mobile || 0) +
                  (item.tablet || 0),
              }));

              folder.file(
                `${campaignOrder.name.replace(/[\W_]/g, '_')}-Sessions_Device_Breakdown.csv`,
                Papa.unparse(exportData)
              );

              zip.generateAsync({ type: 'blob' }).then(
                (blob) => {
                  saveAs(
                    blob,
                    `${campaignOrder.name.replace(/[\W_]/g, '_')}-Sessions_Device_Breakdown`
                  );
                  setExportButtonLoading(false);
                },
                (error) => {
                  console.error('Export error:', error);
                  setExportButtonLoading(false);
                }
              );
            } catch (error) {
              console.error('CSV export error:', error);
              setExportButtonLoading(false);
            }
          },
        },
        {
          key: 'allSessions',
          text: 'Sessions - With Events',
          tooltip:
            'Export all individual session records. This may take a moment for large datasets.',
          hasConfirm: true,
          confirmTitle: 'Export All Sessions',
          confirmDescription: 'Exporting all sessions with events may take a while for large datasets. Do you want to continue?',
          onClick: () => {
            setExportButtonLoading(true);
            setIsExportRequested(true);
            getAllSessions({
              variables: {
                where: buildSessionsWhereClause(),
                orderBy: 'start_ASC',
              },
            });
          },
        },
      ]);
    }
  }, [
    setExportButtonMenuItems,
    setExportButtonDisable,
    setExportButtonLoading,
    campaignOrder,
    sessions,
    getAllSessions,
    currentDateRange,
    buildSessionsWhereClause,
  ]);

  // If export functions exist but no campaignOrder yet, disable the button
  useEffect(() => {
    if (setExportButtonDisable && !campaignOrder) {
      setExportButtonDisable(true);
    }
  }, [setExportButtonDisable, campaignOrder]);

  // Handle export when allSessionsData is loaded
  useEffect(() => {
    if (
      isExportRequested &&
      allSessionsData &&
      allSessionsData.campaignOrderSessionsWithEventses &&
      !allSessionsLoading
    ) {
      try {
        const zip = new JSZip();
        const folder = zip.folder(`${campaignOrder.name.replace(/[\W_]/g, '_')}-All_Sessions`);

        // Access the correct data structure from the GraphQL query
        const sessionsData = allSessionsData.campaignOrderSessionsWithEventses || [];

        if (sessionsData.length === 0) {
          console.warn('No session data available to export');
          setExportButtonLoading(false);
          setIsExportRequested(false);
          return;
        }

        const exportData = sessionsData.map((session) => ({
          'Session ID': session.sessionId || '',
          'Start Time': session.start ? moment(session.start).format('YYYY-MM-DD HH:mm:ss') : '',
          'End Time': session.end ? moment(session.end).format('YYYY-MM-DD HH:mm:ss') : '',
          'Duration (seconds)': session.durationInSeconds || 0,
          'Duration': session.durationInSeconds ? secondsToTimeText(session.durationInSeconds) : '0s',
          'Page Views': session.pageViews || 0,
          'Device Type': session.deviceType || 'Unknown',
          'Total Impressions': session.totalImpressions || 0,
          'Landing Page': session.landingPage || '',
          'Exit Page': session.exitPage || '',
          'Attribution Method': session.attrMethod || '',
          'Attribution Value': session.attrValue || '',
          'Attribution Window': session.attrWindow || '',
          'Impressions': JSON.stringify(session.impressions, (key, value) => key === '__typename' ? undefined : value),
          'Session Journey': JSON.stringify(session.sessionJourney, (key, value) => key === '__typename' ? undefined : value),
        }));

        folder.file(
          `${campaignOrder.name.replace(/[\W_]/g, '_')}-All_Sessions.csv`,
          Papa.unparse(exportData)
        );

        zip.generateAsync({ type: 'blob' }).then(
          (blob) => {
            saveAs(blob, `${campaignOrder.name.replace(/[\W_]/g, '_')}-All_Sessions`);
            setExportButtonLoading(false);
            setIsExportRequested(false);
          },
          (error) => {
            console.error('Export error:', error);
            setExportButtonLoading(false);
            setIsExportRequested(false);
          }
        );
      } catch (error) {
        console.error('CSV export error:', error);
        setExportButtonLoading(false);
        setIsExportRequested(false);
      }
    }
  }, [allSessionsData, allSessionsLoading, campaignOrder, setExportButtonLoading, isExportRequested]);

  useEffect(() => {
    if (!sessions) return;

    if (currentDateRange && sessions.byDate) {
      const dataToReduce = sessions.byDate.filter((item) =>
        filterByDateRange(item, currentDateRange)
      );

      const totals = dataToReduce.reduce(
        (acc, item) => {
          acc.sessions += item.sessions || 0;
          acc.totalSessionDuration += item.totalSessionDuration || 0;
          acc.totalPagesPerSession += (item.pagesPerSession || 0) * (item.sessions || 0);
          return acc;
        },
        { sessions: 0, totalSessionDuration: 0, totalPagesPerSession: 0 }
      );

      setSummaryData({
        sessions: totals.sessions,
        averageSessionDuration:
          totals.sessions > 0 ? totals.totalSessionDuration / totals.sessions : 0,
        pagesPerSession: totals.sessions > 0 ? totals.totalPagesPerSession / totals.sessions : 0,
      });
    }
  }, [currentDateRange, sessions]);

  // Early return if no data
  if (!sessionsList) {
    return (
      <React.Fragment>
        {React.cloneElement(props.children, {
          summaryData,
          formattedByDate: [],
          formattedSessions: [],
          filteredByDateBreakdown: [],
          sessionsTotal: 0,
          changeDatePicker,
          loading,
          sessionsLoading,
          getAllSessions,
          allSessionsData,
          allSessionsLoading,
          allSessionsError,
          fetchSessionDetails,
          expandedRowsData,
          setExpandedRowsData,
          sessionDetailsLoading,
          setExportButtonLoading,
          setExportButtonDisable,
          setExportButtonMenuItems,
        })}
      </React.Fragment>
    );
  }
  const { campaignOrderSessionsWithEventsesConnection, full } = sessionsList;

  // Format by Date for chart and for easier data manipulation
  const formattedByDate =
    sessions && sessions.byDate
      ? sessions.byDate
          .filter((item) => filterByDateRange(item, currentDateRange))
          .map((item) => ({
            ...item,
            xAxis: item.date,
          }))
      : [];

  // Filter out unnecessary data on connection query
  const formattedSessions = (campaignOrderSessionsWithEventsesConnection?.edges || []).map(
    (edge) => edge.node
  );

  // Filter byDateBreakdown based on current date range
  const filteredByDateBreakdown =
    sessions && sessions.byDateBreakdown
      ? sessions.byDateBreakdown.filter((item) => filterByDateRange(item, currentDateRange))
      : [];

  return React.cloneElement(props.children, {
    ...props.children.props,
    summaryData,
    formattedByDate,
    formattedSessions,
    filteredByDateBreakdown,
    sessionsTotal: full.aggregate.count,
    changeDatePicker,
    loading,
    sessionsLoading,
    getAllSessions,
    allSessionsData,
    allSessionsLoading,
    allSessionsError,
    fetchSessionDetails,
    expandedRowsData,
    setExpandedRowsData,
    sessionDetailsLoading,
    setExportButtonLoading,
    setExportButtonDisable,
    setExportButtonMenuItems,
  });
};

export default withRouter(SessionsController);
